import React, { useState, useEffect } from 'react';
import { Briefcase, CheckCircle2, Euro, Loader2, X, UploadCloud, Search, PlayCircle } from 'lucide-react';

export default function MitarbeiterAuftrage() {
  const [activeTab, setActiveTab] = useState('available');
  const [jobs, setJobs] = useState({ available: [], active: [], in_review: [], completed: [] });
  const [isLoading, setIsLoading] = useState(true);
  
  const [activeTestModal, setActiveTestModal] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => { fetchJobs(); }, []);

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('tester_token');
      const response = await fetch('/api/employee/jobs', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setJobs(await response.json());
    } catch (err) { console.error(err); } finally { setIsLoading(false); }
  };

  const handleAcceptJob = async (jobId) => {
    try {
      const token = localStorage.getItem('tester_token');
      const response = await fetch(`/api/employee/jobs/${jobId}/accept`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchJobs();
        setActiveTab('active');
      }
    } catch (err) { console.error(err); }
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!selectedFile) return alert('Bitte lade deinen Test-Report hoch.');
    
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const token = localStorage.getItem('tester_token');
      const response = await fetch(`/api/employee/jobs/${activeTestModal.id}/submit-report`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        setActiveTestModal(null);
        setSelectedFile(null);
        fetchJobs();
        setActiveTab('in_review');
      }
    } catch (err) { console.error(err); } finally { setIsUploading(false); }
  };

  if (isLoading) return <div className="flex justify-center items-center h-64"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-primary">Meine Testaufträge</h1>
        <p className="text-muted-foreground">Verwalten Sie Ihre Evaluierungen und laden Sie Reports hoch.</p>
      </div>

      <div className="flex gap-4 border-b border-border mb-6 overflow-x-auto">
        <button onClick={() => setActiveTab('available')} className={`pb-3 font-medium border-b-2 whitespace-nowrap ${activeTab === 'available' ? 'border-accent text-accent' : 'border-transparent text-secondary'}`}>
          Neu zugewiesen ({jobs.available.length})
        </button>
        <button onClick={() => setActiveTab('active')} className={`pb-3 font-medium border-b-2 whitespace-nowrap ${activeTab === 'active' ? 'border-accent text-accent' : 'border-transparent text-secondary'}`}>
          In Bearbeitung ({jobs.active.length})
        </button>
        <button onClick={() => setActiveTab('in_review')} className={`pb-3 font-medium border-b-2 whitespace-nowrap ${activeTab === 'in_review' ? 'border-amber-500 text-amber-600' : 'border-transparent text-secondary'}`}>
          Wird geprüft ({jobs.in_review?.length || 0})
        </button>
        <button onClick={() => setActiveTab('completed')} className={`pb-3 font-medium border-b-2 whitespace-nowrap ${activeTab === 'completed' ? 'border-green-500 text-green-600' : 'border-transparent text-secondary'}`}>
          Abgeschlossen ({jobs.completed.length})
        </button>
      </div>

      <div className="space-y-4">
        {activeTab === 'available' && jobs.available.map(job => (
          <div key={job.id} className="bg-white p-6 rounded-xl border border-border shadow-sm flex flex-col sm:flex-row justify-between gap-4">
            <div>
              <span className="text-xs font-mono text-muted-foreground">{job.id}</span>
              <h3 className="font-bold text-primary text-lg">{job.title}</h3>
              <p className="text-sm text-muted-foreground">Branche: {job.type} • Dauer: {job.duration}</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Vergütung</p>
                <p className="font-bold text-green-600 text-lg">{job.reward}</p>
              </div>
              <button onClick={() => handleAcceptJob(job.id)} className="bg-primary text-white px-5 py-2.5 rounded-md font-medium hover:bg-secondary flex items-center gap-2 text-sm shadow-sm">
                <PlayCircle className="w-4 h-4" /> Test starten
              </button>
            </div>
          </div>
        ))}

        {activeTab === 'active' && jobs.active.map(job => (
          <div key={job.id} className="bg-sky-50 p-6 rounded-xl border border-sky-200 shadow-sm flex flex-col sm:flex-row justify-between gap-4 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-accent"></div>
            <div><span className="text-xs font-mono text-muted-foreground">{job.id}</span><h3 className="font-bold text-primary text-lg">{job.title}</h3></div>
            <button onClick={() => setActiveTestModal(job)} className="bg-accent text-white px-6 py-2 rounded-md font-medium hover:bg-accent/90 flex items-center gap-2 shadow-sm"><UploadCloud className="w-4 h-4" /> Report einreichen</button>
          </div>
        ))}

        {activeTab === 'in_review' && jobs.in_review?.map(job => (
          <div key={job.id} className="bg-amber-50 p-6 rounded-xl border border-amber-200 flex justify-between items-center opacity-80">
            <div><h3 className="font-bold text-amber-800">{job.title}</h3><p className="text-sm text-amber-700">Dein Report wird aktuell vom Admin geprüft.</p></div>
            <div className="flex items-center gap-2 text-amber-700 font-bold bg-amber-100 px-4 py-2 rounded-md"><Search className="w-4 h-4" /> {job.status || 'Wird geprüft'}</div>
          </div>
        ))}

        {activeTab === 'completed' && jobs.completed.map(job => (
          <div key={job.id} className="bg-slate-50 p-6 rounded-xl border border-border flex justify-between items-center opacity-75">
            <div><div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-green-600" /><h3 className="font-bold text-primary">{job.title}</h3></div><p className="text-sm text-muted-foreground">Abgeschlossen am {job.date}</p></div>
            <div className="flex items-center gap-2 text-green-600 font-bold bg-green-100 px-4 py-2 rounded-md"><Euro className="w-4 h-4" /> +{job.reward}</div>
          </div>
        ))}

        {jobs[activeTab]?.length === 0 && <div className="text-center py-12 bg-white rounded-xl border border-dashed"><p className="text-muted-foreground">Keine Aufträge in dieser Kategorie vorhanden.</p></div>}
      </div>

      {activeTestModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full flex flex-col animate-in zoom-in-95">
            <div className="flex justify-between items-center p-4 border-b border-border">
              <div><h2 className="font-bold text-lg text-primary">Report einreichen</h2><p className="text-xs font-mono text-muted-foreground">Auftrag: {activeTestModal.title}</p></div>
              <button onClick={() => {setActiveTestModal(null); setSelectedFile(null);}} className="text-slate-400 hover:text-red-500"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleSubmitReport}>
              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-md text-blue-800 text-sm">
                  Lade hier deinen finalen Test-Report hoch, damit wir dir die <strong>{activeTestModal.reward}</strong> auf dein Guthabenkonto auszahlen können.
                </div>
                <label className="border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 bg-white">
                  <input type="file" className="hidden" accept=".txt,.pdf,.doc,.docx" onChange={(e) => setSelectedFile(e.target.files[0])} />
                  <UploadCloud className="w-8 h-8 text-accent mb-3" />
                  <span className="font-medium text-primary text-center">
                    {selectedFile ? selectedFile.name : 'Klicke hier, um deine Datei auszuwählen'}
                  </span>
                </label>
              </div>
              <div className="p-4 border-t border-border flex justify-end gap-3 bg-slate-50 rounded-b-xl">
                <button type="button" onClick={() => setActiveTestModal(null)} className="px-4 py-2 text-slate-600 bg-white border border-border hover:bg-slate-50 rounded font-medium">Abbrechen</button>
                <button type="submit" disabled={!selectedFile || isUploading} className="px-4 py-2 text-white bg-primary hover:bg-secondary rounded font-medium flex items-center gap-2 disabled:opacity-50">
                  {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Report absenden
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}